// On-screen debug console for devices where real dev tools are out of reach
// (an iPhone rented from a device cloud, a borrowed Android). Enabled with
// ?debug=true, no-op otherwise.
//
// It captures console.log/info/warn/error plus uncaught errors and promise
// rejections into a ring buffer, renders them in a floating panel, and can
// POST the whole buffer to the api's debug paste drop (/api/v1/debug/paste,
// enabled with DEBUG_PASTE=true) — so the loop on a cloud device is: load the
// page, watch the panel, tap "Send", read the log out of api/debug/ locally.
// The paste endpoint is dev-only, so in production the panel is read-only and
// the answer is a screenshot.
//
// Deliberately raw DOM, installed before React renders: a crash during app
// boot is exactly the kind of failure this exists to catch, so it can depend
// on nothing but the document.

const MAX_LINES = 800
const MAX_LINE_LENGTH = 2000
// Uncaught errors auto-upload so a dying page still phones home, but a render
// loop that throws per-frame must not carpet-bomb the repo.
const MAX_AUTO_SENDS = 3
const AUTO_SEND_INTERVAL_MS = 10000
// Console traffic can be per-frame (heartbeats, layout storms) — repaints are
// coalesced onto this interval instead of thrashing the DOM per line.
const RENDER_COALESCE_MS = 250

const PASTE_ENDPOINT = '/api/v1/debug/paste'

type Level = 'log' | 'info' | 'warn' | 'error'

interface DebugConsole {
  lines: string[]
  errorCount: number
}

declare global {
  interface Window {
    __forumDebugConsole?: DebugConsole
  }
}

// Which build produced this log. vite content-hashes the bundle filename, so
// `index-DLqy-bVK.js` changes whenever the code does — enough to tell two runs
// apart at a glance. This exists because a log was once analysed twice under
// the belief it came from two different builds, costing a full test round on a
// device that is slow to get hold of. The git SHA would be nicer, but .git
// never reaches the frontend stage of the Docker build.
function buildId(): string {
  try {
    const fromModule = import.meta.url
    if (fromModule) return fromModule.split('/').pop() || fromModule
  } catch { /* import.meta unavailable */ }
  try {
    const tag = document.querySelector('script[type="module"][src]') as HTMLScriptElement | null
    if (tag?.src) return tag.src.split('/').pop() || tag.src
  } catch { /* no document yet */ }
  return '(unknown)'
}

function formatArg(arg: unknown): string {
  if (typeof arg === 'string') return arg
  if (arg instanceof Error) return `${arg.name}: ${arg.message}${arg.stack ? `\n${arg.stack}` : ''}`
  try {
    return JSON.stringify(arg)
  } catch {
    return String(arg)
  }
}

// A diagnostic tool must never be the reason a page fails. This runs before
// React mounts, so anything thrown here aborts module evaluation and the app
// never renders — which is indistinguishable from the blank-screen bugs it
// exists to diagnose. Hence the blanket catch.
export function installDebugConsole(): void {
  try {
    install()
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[debugConsole] failed to install; continuing without it', e)
  }
}

function install(): void {
  let enabled = false
  try {
    enabled = new URLSearchParams(window.location.search).get('debug') === 'true'
  } catch {
    return
  }
  if (!enabled || window.__forumDebugConsole) return

  const state: DebugConsole = { lines: [], errorCount: 0 }
  window.__forumDebugConsole = state
  const t0 = performance.now()

  let autoSends = 0
  let lastAutoSend = 0

  // ORDER MATTERS BELOW. Everything is `const` arrow functions, so a call made
  // before its declaration is evaluated hits the temporal dead zone and throws
  // ReferenceError. The overlay must therefore be built before capture is
  // installed, because push() paints, and capture must be installed before the
  // first push(). Do not reorder these three sections.

  // ---- overlay ----

  const HOST_STYLE =
    'position:fixed;z-index:2147483647;left:0;right:0;bottom:0;' +
    'font:10px/1.5 ui-monospace,Menlo,monospace;color:#e6e8ec;pointer-events:none'

  const host = document.createElement('div')
  host.setAttribute('style', HOST_STYLE)

  const pill = document.createElement('button')
  pill.setAttribute(
    'style',
    'pointer-events:auto;position:absolute;bottom:10px;left:10px;padding:6px 12px;' +
      'border-radius:14px;border:1px solid #4a5260;background:rgba(20,22,26,.92);' +
      'color:#e6e8ec;font:600 11px ui-monospace,Menlo,monospace',
  )

  const panel = document.createElement('div')
  panel.setAttribute(
    'style',
    'pointer-events:auto;display:none;background:rgba(10,12,15,.94);' +
      'border-top:1px solid #333944;max-height:45vh;flex-direction:column',
  )

  const bar = document.createElement('div')
  bar.setAttribute('style', 'display:flex;gap:8px;align-items:center;padding:6px 8px;border-bottom:1px solid #262a31')
  const mkButton = (label: string) => {
    const b = document.createElement('button')
    b.textContent = label
    b.setAttribute(
      'style',
      'padding:5px 12px;border-radius:6px;border:1px solid #4a5260;background:#262a31;' +
        'color:#e6e8ec;font:600 11px ui-monospace,Menlo,monospace',
    )
    return b
  }
  const sendBtn = mkButton('Send logs')
  const clearBtn = mkButton('Clear')
  const hideBtn = mkButton('Hide')
  const statusEl = document.createElement('span')
  statusEl.setAttribute('style', 'font-size:10px;color:#99a0ac;overflow:hidden;text-overflow:ellipsis;white-space:nowrap')
  bar.append(sendBtn, clearBtn, hideBtn, statusEl)

  const logEl = document.createElement('pre')
  logEl.setAttribute(
    'style',
    'margin:0;padding:6px 8px;overflow:auto;flex:1;white-space:pre-wrap;word-break:break-word;' +
      '-webkit-overflow-scrolling:touch',
  )
  panel.append(bar, logEl)
  host.append(panel, pill)

  const setStatus = (msg: string) => {
    statusEl.textContent = msg
  }

  let open = false
  const syncOpen = () => {
    panel.style.display = open ? 'flex' : 'none'
    pill.style.display = open ? 'none' : 'block'
  }

  const renderPill = () => {
    pill.textContent = `🐞 ${state.lines.length}${state.errorCount ? ` ⚠︎${state.errorCount}` : ''}`
    pill.style.borderColor = state.errorCount ? '#c94f4f' : '#4a5260'
  }
  const renderLines = () => {
    if (!open) return
    const atBottom = logEl.scrollTop + logEl.clientHeight >= logEl.scrollHeight - 24
    logEl.textContent = state.lines.join('\n')
    if (atBottom) logEl.scrollTop = logEl.scrollHeight
  }

  let renderQueued = false
  const scheduleRender = () => {
    if (renderQueued) return
    renderQueued = true
    setTimeout(() => {
      renderQueued = false
      renderPill()
      renderLines()
    }, RENDER_COALESCE_MS)
  }

  // ---- upload ----

  const payloadText = () =>
    [
      `url: ${window.location.href}`,
      `build: ${buildId()}`,
      `userAgent: ${navigator.userAgent}`,
      `viewport: ${window.innerWidth}x${window.innerHeight} dpr ${window.devicePixelRatio}`,
      `sent: ${new Date().toISOString()}`,
      '',
      ...state.lines,
    ].join('\n')

  const send = (reason: string): Promise<string> =>
    fetch(PASTE_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: `device-log-${reason}`, text: payloadText() }),
    }).then(
      // A 404 is the normal answer in production, where the paste endpoint is
      // deliberately not enabled — say so rather than implying a bug.
      (r) => (r.ok ? 'sent ✓' : r.status === 404 ? 'upload disabled here — screenshot instead' : `failed (${r.status})`),
      (e) => `failed (${(e as Error).message})`,
    )

  const autoSend = (reason: string) => {
    const now = Date.now()
    if (autoSends >= MAX_AUTO_SENDS || now - lastAutoSend < AUTO_SEND_INTERVAL_MS) return
    autoSends++
    lastAutoSend = now
    send(`auto-${reason.replace(/\s+/g, '-')}`).then((status) => setStatus(`auto-upload: ${status}`))
  }

  pill.onclick = () => { open = true; syncOpen(); renderLines() }
  hideBtn.onclick = () => { open = false; syncOpen() }
  clearBtn.onclick = () => { state.lines.length = 0; state.errorCount = 0; renderLines(); renderPill() }
  sendBtn.onclick = () => {
    setStatus('sending…')
    send('manual').then(setStatus)
  }

  // ---- capture ----

  const push = (level: Level, parts: unknown[]) => {
    const stamp = ((performance.now() - t0) / 1000).toFixed(3)
    let line = `+${stamp} [${level}] ${parts.map(formatArg).join(' ')}`
    if (line.length > MAX_LINE_LENGTH) line = line.slice(0, MAX_LINE_LENGTH) + '…'
    state.lines.push(line)
    if (state.lines.length > MAX_LINES) state.lines.splice(0, state.lines.length - MAX_LINES)
    if (level === 'error') state.errorCount++
    scheduleRender()
  }

  const levels: Level[] = ['log', 'info', 'warn', 'error']
  for (const level of levels) {
    const original = console[level].bind(console)
    console[level] = (...args: unknown[]) => {
      original(...args)
      // Never let a fault in the capture path break the caller's own logging.
      try {
        push(level, args)
      } catch {
        /* ignore */
      }
    }
  }

  window.addEventListener('error', (e) => {
    push('error', [`UNCAUGHT ${e.message} (${e.filename}:${e.lineno}:${e.colno})`, e.error])
    autoSend('uncaught error')
  })
  window.addEventListener('unhandledrejection', (e) => {
    push('error', ['UNHANDLED REJECTION', e.reason])
    autoSend('unhandled rejection')
  })

  // ---- attach ----

  const attach = () => {
    document.body.appendChild(host)
    renderPill()
    syncOpen()
  }
  if (document.body) attach()
  else document.addEventListener('DOMContentLoaded', attach)

  push('info', [
    `debug console installed — ${navigator.userAgent}`,
    `viewport ${window.innerWidth}x${window.innerHeight} dpr ${window.devicePixelRatio} touchPoints ${navigator.maxTouchPoints}`,
    `build ${buildId()} loaded ${window.location.href}`,
  ])
}
